import React from "react";
import { TooltipButton } from "../utilities/ToolTipButton";

type FormFieldRowProps = {
  id: string;
  label: string;
  required?: boolean;
  error?: string;
  isWritten?: boolean;
  readOnly?: boolean;

  tooltip?: string;

  children: React.ReactNode;
};

function FormFieldRow({
  id,
  label,
  required = false,
  error,
  isWritten = false,
  readOnly = false,
  tooltip = undefined,
  children,
}: FormFieldRowProps) {
  const fieldStateClass = error
    ? "mtam-formField--error"
    : isWritten
      ? "mtam-formField--written"
      : "mtam-formField--default";
  const errorId = error ? `${id}-error` : undefined;
  const labelId = `${id}-label`;
  const childProps = React.isValidElement(children)
    ? (children.props as Record<string, unknown>)
    : {};

  const control =
    React.isValidElement(children) && !readOnly
      ? React.cloneElement(children as React.ReactElement<any>, {
          "aria-describedby":
            [childProps["aria-describedby"], errorId]
              .filter(Boolean)
              .join(" ") || undefined,
          "aria-invalid": error ? true : undefined,
        })
      : children;

  if (readOnly) {
    return (
      <div className="mtam-formFieldWrap">
        <div
          className={`mtam-formField ${fieldStateClass} mtam-formField--readonly`}
          role="group"
          aria-labelledby={labelId}
        >
          <span className="mtam-formField__label">
            <span id={labelId} className="mtam-formField__labelText">{label}</span>

            {tooltip ? (
              <TooltipButton
                label="?"
                tooltip={tooltip}
                disabled={true}
                buttonClassName="mtam-tooltipButton"
                buttonIcon={false}
              />
            ) : null}
          </span>

          <span className="mtam-formField__control">{control}</span>
        </div>

        {error ? (
          <p id={errorId} className="mtam-formField__error" aria-live="polite">
            {error}
          </p>
        ) : null}
      </div>
    );
  }

  return (
    <div className="mtam-formFieldWrap">
      <label
        htmlFor={id}
        className={`mtam-formField ${fieldStateClass}`}
      >
        <span className="mtam-formField__label">
          <span className="mtam-formField__labelText">{label}</span>

          {tooltip ? (
            <TooltipButton
              label="?"
              tooltip={tooltip}
              disabled={true}
              buttonClassName="mtam-tooltipButton"
              buttonIcon={false}
            />
          ) : null}
        </span>

        <span className="mtam-formField__control">{control}</span>
      </label>

      {error ? (
        <p id={errorId} className="mtam-formField__error" aria-live="polite">
          {error}
        </p>
      ) : null}
    </div>
  );
}

export default FormFieldRow;
